import { Bar, BarChart, XAxis, YAxis, CartesianGrid } from 'recharts'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Sale } from '@/services/sales'
import { format } from 'date-fns'
import { pt } from 'date-fns/locale'

interface RevenueChartProps {
  sales: Sale[]
}

const chartConfig = {
  revenue: {
    label: 'Receita (€)',
    color: 'hsl(var(--primary))',
  },
} satisfies ChartConfig

export const RevenueChart = ({ sales }: RevenueChartProps) => {
  // Aggregate sales by date
  const aggregatedData = sales.reduce(
    (acc, sale) => {
      const dateKey = format(new Date(sale.created_at), 'd MMM', { locale: pt })
      if (!acc[dateKey]) {
        acc[dateKey] = { date: dateKey, revenue: 0 }
      }
      acc[dateKey].revenue += sale.amount
      return acc
    },
    {} as Record<string, { date: string; revenue: number }>,
  )

  const chartData = Object.values(aggregatedData)

  return (
    <Card>
      <CardHeader>
        <CardTitle>Receita</CardTitle>
        <CardDescription>
          Vendas acumuladas no período selecionado.
        </CardDescription>
      </CardHeader>
      <CardContent className="pl-2">
        {chartData.length > 0 ? (
          <ChartContainer config={chartConfig} className="h-[300px] w-full">
            <BarChart data={chartData}>
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                stroke="#888888"
                fontSize={12}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                stroke="#888888"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => `€${value}`}
              />
              <ChartTooltip
                cursor={{ fill: 'hsl(var(--muted))' }}
                content={<ChartTooltipContent />}
              />
              <Bar
                dataKey="revenue"
                fill="currentColor"
                radius={[4, 4, 0, 0]}
                className="fill-primary"
              />
            </BarChart>
          </ChartContainer>
        ) : (
          <div className="h-[300px] flex items-center justify-center text-muted-foreground">
            Sem dados para apresentar neste período.
          </div>
        )}
      </CardContent>
    </Card>
  )
}
